/**
 * Authentication, in MusicMentor's own vocabulary.
 *
 * django-allauth's headless API is a good implementation of a hard problem, but
 * it is a third-party contract: its paths live under `/_allauth/browser/v1`, its
 * successes come back as `{status, data, meta}`, its failures as `{status,
 * errors:[{param, code, message}]}`, and a partly-authenticated session is
 * signalled by a 401 that is not really a failure. None of that should reach a
 * React component.
 *
 * So nothing outside this module mentions allauth. Screens call
 * `auth.signIn(...)` and receive `AuthState`; they never see a path, a flow
 * array, or a `status` integer. If allauth is ever replaced, this file changes
 * and no screen does.
 *
 * Types come from the pinned specification (`allauth-openapi.json`, exported by
 * the backend from allauth 65.18.0), not from paths typed out by hand. A drift
 * test fails if the installed package stops matching.
 *
 * Same origin and same security policy as `/api/v1`: session cookie, CSRF
 * header, no token anywhere a script can read.
 */

import { API_ORIGIN, ApiError, readCookie, type ApiErrorKind, type FieldError } from './client.ts';

// The same origin as `/api/v1`: two logical clients, one service, one cookie
// and CSRF policy. Splitting them across origins would break the session.
const AUTH_BASE = `${API_ORIGIN}/_allauth/browser/v1`;

/** What the app needs to know about who is signed in. */
export type AuthStatus =
  | 'signed-out'
  | 'signed-in'
  /** Authenticated but the account still needs an email confirmed. */
  | 'verification-required'
  /** A password reset key was accepted; a new password is expected next. */
  | 'reset-pending';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  hasVerifiedEmail: boolean;
}

export interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  /** Address awaiting confirmation, for the "check your email" screen. */
  pendingEmail: string | null;
}

export const SIGNED_OUT: AuthState = { status: 'signed-out', user: null, pendingEmail: null };

/** One address on the account. `primary` is the one that signs you in. */
export interface EmailAddress {
  email: string;
  verified: boolean;
  primary: boolean;
}

/** Options every call accepts. Both are optional and both are commonly absent. */
export interface AuthCallOptions {
  signal?: AbortSignal;
  turnstileToken?: string;
}

/** allauth's flow ids, kept in this file and nowhere else. */
const FLOW_VERIFY_EMAIL = 'verify_email';
const FLOW_PASSWORD_RESET = 'password_reset_by_code';

interface AllauthEnvelope {
  status: number;
  data?:
    | {
        user?: { id?: unknown; email?: string; display?: string; has_usable_password?: boolean };
        flows?: { id?: string; is_pending?: boolean }[];
        email?: string;
      }
    // The email endpoints answer with a bare array in `data`, not an object.
    | { email?: string; verified?: boolean; primary?: boolean }[];
  meta?: { is_authenticated?: boolean };
  errors?: { param?: string; code?: string; message?: string }[];
}

function toEmailAddresses(envelope: AllauthEnvelope): EmailAddress[] {
  const data = envelope.data;
  if (!Array.isArray(data)) return [];
  return data.map((row) => ({
    email: String(row.email ?? ''),
    verified: Boolean(row.verified),
    primary: Boolean(row.primary),
  }));
}

/**
 * Map an allauth session envelope onto our own state.
 *
 * The subtlety worth naming: allauth answers `/auth/session` with **401** when a
 * signup has happened but email is unconfirmed. That is not an error — the
 * account exists and the person is part-way through — so it is translated to
 * `verification-required` rather than being thrown.
 */
export function toAuthState(envelope: AllauthEnvelope): AuthState {
  // The email endpoints put an array here. Those never describe a session, so
  // an array is simply "nothing to learn about who is signed in".
  const data = Array.isArray(envelope.data) ? {} : (envelope.data ?? {});
  const user = data.user;
  const pending = (data.flows ?? []).filter((flow) => flow.is_pending);

  const account: AuthUser | null = user?.email
    ? {
        id: String(user.id ?? ''),
        email: String(user.email),
        displayName: String(user.display ?? ''),
        hasVerifiedEmail: !pending.some((flow) => flow.id === FLOW_VERIFY_EMAIL),
      }
    : null;

  if (pending.some((flow) => flow.id === FLOW_PASSWORD_RESET)) {
    return { status: 'reset-pending', user: account, pendingEmail: data.email ?? null };
  }
  if (pending.some((flow) => flow.id === FLOW_VERIFY_EMAIL)) {
    return {
      status: 'verification-required',
      user: account,
      pendingEmail: account?.email ?? data.email ?? null,
    };
  }
  if (envelope.meta?.is_authenticated && account) {
    return { status: 'signed-in', user: account, pendingEmail: null };
  }
  return SIGNED_OUT;
}

/** allauth error codes → the wording a learner reads. */
const MESSAGES: Record<string, string> = {
  email_password_mismatch: 'That email and password do not match an account.',
  email_taken: 'There is already an account with that email address.',
  password_too_short: 'Passwords need at least 10 characters.',
  password_too_common: 'That password is too common. Choose something less predictable.',
  password_entirely_numeric: 'Passwords cannot be only numbers.',
  password_too_similar_to_email: 'Your password is too close to your email address.',
  too_many_login_attempts: 'Too many attempts. Wait a few minutes and try again.',
  invalid_or_expired_key: 'That link has expired. Request a new one.',
  captcha_failed: 'Challenge verification failed. Try again.',
  incorrect_code: 'That code is not right.',
  cannot_remove_primary_email: 'This is your sign-in address, so it cannot be removed.',
  cannot_delete_only_email: 'An account needs at least one email address.',
  unverified_primary_email: 'Confirm this address before making it your sign-in address.',
  enter_current_password: 'Enter your current password.',
  incorrect_password: 'That password is not right.',
};

/** Server-side password rules, so the checklist matches what is enforced. */
export const PASSWORD_MIN_LENGTH = 10;

export interface AuthClientOptions {
  baseUrl?: string;
  timeoutMs?: number;
  onStateChange?: (state: AuthState) => void;
  fetchImpl?: typeof fetch;
}

export class AuthClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;
  private readonly onStateChange?: (state: AuthState) => void;
  private readonly fetchImpl: typeof fetch;

  constructor(options: AuthClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? AUTH_BASE).replace(/\/$/, '');
    this.timeoutMs = options.timeoutMs ?? 15_000;
    this.onStateChange = options.onStateChange;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  private async call(
    method: string,
    path: string,
    body?: unknown,
    options: {
      signal?: AbortSignal;
      allowUnauthenticated?: boolean;
      /**
       * Cloudflare Turnstile proof, for the three endpoints the backend's
       * `TurnstileMiddleware` guards. Sent as a header rather than in the body so
       * the request payload stays exactly the vendor's documented shape.
       */
      turnstileToken?: string;
    } = {},
  ): Promise<AllauthEnvelope> {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      throw new ApiError('offline', 'You are offline. Your work is still saved on this iPad.');
    }

    const controller = new AbortController();
    const timer = setTimeout(
      () => controller.abort(new DOMException('timeout', 'TimeoutError')),
      this.timeoutMs,
    );
    const forward = () => controller.abort(options.signal?.reason);
    options.signal?.addEventListener('abort', forward, { once: true });

    const headers = new Headers({ Accept: 'application/json' });
    if (method !== 'GET') {
      headers.set('Content-Type', 'application/json');
      const csrf = readCookie('mm_csrftoken');
      if (csrf) headers.set('X-CSRFToken', csrf);
    }
    if (options.turnstileToken) headers.set('X-Turnstile-Token', options.turnstileToken);

    let response: Response;
    try {
      response = await this.fetchImpl(`${this.baseUrl}${path}`, {
        method,
        headers,
        credentials: 'include',
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal,
      });
    } catch (error) {
      if (options.signal?.aborted) throw new ApiError('cancelled', 'Request cancelled.');
      if ((error as Error)?.name === 'TimeoutError' || controller.signal.aborted) {
        throw new ApiError('timeout', 'The server took too long to answer.');
      }
      throw new ApiError('offline', 'Could not reach the server.');
    } finally {
      clearTimeout(timer);
      options.signal?.removeEventListener('abort', forward);
    }

    const text = await response.text();
    let envelope: AllauthEnvelope = { status: response.status };
    if (text) {
      try {
        envelope = { ...(JSON.parse(text) as AllauthEnvelope), status: response.status };
      } catch {
        /* keep the bare status */
      }
    }

    // 401 with pending flows is a *state*, not a failure: the account exists and
    // the person is mid-journey. Everything else at 401 is genuinely signed out.
    const flows = Array.isArray(envelope.data) ? [] : (envelope.data?.flows ?? []);
    const pending = flows.some((flow) => flow.is_pending);
    if (response.ok || (response.status === 401 && (pending || options.allowUnauthenticated))) {
      return envelope;
    }

    const fields: FieldError[] = (envelope.errors ?? []).map((error) => ({
      field: error.param ?? '',
      message: MESSAGES[error.code ?? ''] ?? error.message ?? 'Something went wrong.',
    }));

    const kind: ApiErrorKind =
      response.status === 429
        ? 'rate-limited'
        : response.status === 400 || response.status === 409
          ? 'validation'
          : response.status === 403
            ? 'forbidden'
            : response.status >= 500
              ? 'server'
              : 'unauthenticated';

    throw new ApiError(kind, fields[0]?.message ?? 'Something went wrong.', {
      status: response.status,
      fields,
    });
  }

  private publish(envelope: AllauthEnvelope): AuthState {
    const state = toAuthState(envelope);
    this.onStateChange?.(state);
    return state;
  }

  // ------------------------------------------------------------- the surface

  /** Who is signed in, if anyone. Safe to call on a cold start. */
  async currentState(signal?: AbortSignal): Promise<AuthState> {
    try {
      return this.publish(
        await this.call('GET', '/auth/session', undefined, { signal, allowUnauthenticated: true }),
      );
    } catch (error) {
      if (error instanceof ApiError && error.kind === 'unauthenticated') {
        return this.publish({ status: 401 });
      }
      throw error;
    }
  }

  async signIn(email: string, password: string, options: AuthCallOptions = {}): Promise<AuthState> {
    return this.publish(await this.call('POST', '/auth/login', { email, password }, options));
  }

  async signUp(email: string, password: string, options: AuthCallOptions = {}): Promise<AuthState> {
    return this.publish(await this.call('POST', '/auth/signup', { email, password }, options));
  }

  /** Ends this session only. Local work is untouched. */
  async signOut(signal?: AbortSignal): Promise<AuthState> {
    try {
      await this.call('DELETE', '/auth/session', undefined, {
        signal,
        allowUnauthenticated: true,
      });
    } catch (error) {
      // Already gone is the desired outcome, not a failure to report.
      if (!(error instanceof ApiError && error.kind === 'unauthenticated')) throw error;
    }
    return this.publish({ status: 401 });
  }

  async resendVerificationEmail(email: string, options: AuthCallOptions = {}): Promise<void> {
    // Its own endpoint. The first draft used PUT on /auth/email/verify, which
    // does not exist -- the drift test caught it before a screen ever called it.
    await this.call('POST', '/auth/email/verify/resend', { email }, options);
  }

  /**
   * Confirm an address from the key in the emailed link.
   *
   * The key is a claim, never a proof. A screen that renders "verified" because
   * a key was present in the URL would be confidently wrong for an expired,
   * reused or invented key — so the only way to reach a verified state is for
   * this call to resolve.
   */
  async verifyEmail(key: string, options: AuthCallOptions = {}): Promise<AuthState> {
    return this.publish(await this.call('POST', '/auth/email/verify', { key }, options));
  }

  /**
   * Ask for a reset email. Resolves the same way whether or not the address has
   * an account — telling an anonymous caller which emails are registered is an
   * enumeration oracle.
   */
  async requestPasswordReset(email: string, options: AuthCallOptions = {}): Promise<void> {
    try {
      await this.call('POST', '/auth/password/request', { email }, options);
    } catch (error) {
      // A validation failure here means "no such address", which is precisely
      // what must not be distinguishable from success. Rate limiting and captcha
      // failures still surface: those describe the *caller*, not the address.
      if (error instanceof ApiError && error.kind === 'validation') return;
      throw error;
    }
  }

  async resetPassword(
    key: string,
    password: string,
    options: AuthCallOptions = {},
  ): Promise<AuthState> {
    return this.publish(await this.call('POST', '/auth/password/reset', { key, password }, options));
  }

  /**
   * Change the password of the signed-in account.
   *
   * The server is configured with `ACCOUNT_LOGOUT_ON_PASSWORD_CHANGE = True`, so
   * a success ends this session — every other device is signed out too, which is
   * the point. The resolved state says so rather than leaving the app believing
   * it is still signed in.
   */
  async changePassword(
    current: string,
    next: string,
    options: AuthCallOptions = {},
  ): Promise<AuthState> {
    await this.call(
      'POST',
      '/account/password/change',
      { current_password: current, new_password: next },
      // A **success** answers 401 here, which looks exactly like a failure and
      // is not: allauth ends the session as part of the change and then reports
      // the resulting session state, which is of course unauthenticated. Without
      // this the client threw on every successful password change while the
      // password had in fact been changed — the contract test against the real
      // server is what caught it. A genuine failure (a wrong current password)
      // still answers 400 with errors and still throws.
      { ...options, allowUnauthenticated: true },
    );
    return this.publish({ status: 401 });
  }

  /**
   * Prove it is still the same person, before something sensitive.
   *
   * allauth calls this reauthentication; the product calls it confirming your
   * password. It is required before changing the address the account signs in
   * with, so that a walk-up attacker on an unlocked iPad cannot take the account
   * over in two taps.
   */
  async confirmPassword(password: string, options: AuthCallOptions = {}): Promise<AuthState> {
    return this.publish(await this.call('POST', '/auth/reauthenticate', { password }, options));
  }

  // ------------------------------------------------------- email addresses

  /**
   * Every address on the account.
   *
   * An account can hold more than one because that is how a safe change of
   * address works: the new one is added and confirmed *before* it becomes the
   * one that signs you in. Promoting an unconfirmed address immediately would
   * let a typo lock a learner out of their own account.
   */
  async emailAddresses(options: AuthCallOptions = {}): Promise<EmailAddress[]> {
    const envelope = await this.call('GET', '/account/email', undefined, options);
    return toEmailAddresses(envelope);
  }

  /** Add an address and send a confirmation link to it. Does not change sign-in. */
  async addEmail(email: string, options: AuthCallOptions = {}): Promise<EmailAddress[]> {
    return toEmailAddresses(await this.call('POST', '/account/email', { email }, options));
  }

  /** Make an already-confirmed address the one used to sign in. */
  async makeEmailPrimary(email: string, options: AuthCallOptions = {}): Promise<EmailAddress[]> {
    return toEmailAddresses(
      await this.call('PATCH', '/account/email', { email, primary: true }, options),
    );
  }

  async removeEmail(email: string, options: AuthCallOptions = {}): Promise<EmailAddress[]> {
    return toEmailAddresses(await this.call('DELETE', '/account/email', { email }, options));
  }

  /**
   * End every session, not just this one. Used after a password change and from
   * account settings; a separate endpoint from `signOut`, because "log me out
   * here" and "log me out everywhere" are different promises.
   */
  async signOutEverywhere(signal?: AbortSignal): Promise<AuthState> {
    try {
      await this.call('DELETE', '/auth/sessions', undefined, {
        signal,
        allowUnauthenticated: true,
      });
    } catch (error) {
      if (!(error instanceof ApiError && error.kind === 'unauthenticated')) throw error;
    }
    return this.publish({ status: 401 });
  }
}

/**
 * Which password rules the browser can honestly judge.
 *
 * Length and email-similarity are computable here. "Not a password everyone
 * uses" is Django's `CommonPasswordValidator` checking a 20,000-word list on the
 * server — guessing at it would either show a tick the server contradicts or
 * withhold one already earned, so it stays pending until submission.
 */
export function passwordRequirements(password: string, email: string) {
  const trimmedEmail = email.trim().toLowerCase();
  const local = trimmedEmail.split('@')[0];
  return [
    {
      label: `At least ${PASSWORD_MIN_LENGTH} characters`,
      state: password.length === 0 ? 'unmet' : password.length >= PASSWORD_MIN_LENGTH ? 'met' : 'unmet',
    },
    {
      label: 'Not only numbers',
      state: password.length === 0 ? 'unmet' : /^\d+$/.test(password) ? 'unmet' : 'met',
    },
    {
      label: 'Different from your email address',
      state:
        password.length === 0
          ? 'unmet'
          : local && password.toLowerCase().includes(local)
            ? 'unmet'
            : 'met',
    },
    {
      label: 'Not a password everyone uses',
      state: 'pending' as const,
      pendingNote: 'checked when you submit',
    },
  ] as { label: string; state: 'met' | 'unmet' | 'pending'; pendingNote?: string }[];
}
